import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import classNames from 'classnames';
import React from 'react';
import {debounce} from 'ts-debounce';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {WithoutTypenames} from 'graphql-client/WithoutTypenames';
import {ERROR_LEVEL_TO_COLOR} from 'samples/plate-maps/PlateMapConstants';
import {
  MaybeMixedMeasurementUnit,
  MaybeMixedMeasurementValue,
  MULTIPLE_VALUES_SENTINEL,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {MeasurementValidationErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {PlateMapErrorLevel} from 'samples/plate-maps/validation/ValidationTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';

export interface MeasurementWithMixedValuesInputProps {
  /** Error state for the measurement represented by this input */
  errors: MeasurementValidationErrors;
  /** The label for the input group. */
  label: string;
  /** The value of the value input. */
  value: MaybeMixedMeasurementValue;
  /** A function to be called when the value input changes. */
  onChangeValue: (newValue: number | null) => void;
  /** The value of the units input. */
  unit: MaybeMixedMeasurementUnit;
  /** A function to be called when the unit input changes. */
  onChangeUnit: (newUnit: WithoutTypenames<MeasurementUnit> | null) => void;
  unitTypeFilter: (ut: MeasurementUnit['unitType']) => boolean;
}

/**
 * A controlled input group to display and set the value and units of one or more Measurement. Intended for
 * situations where the Measurement may have different values and it is important to display that information.
 */
export function MeasurementWithMixedValuesInput({
  unitTypeFilter,
  errors,
  label,
  value,
  onChangeValue,
  unit,
  onChangeUnit,
}: MeasurementWithMixedValuesInputProps): JSX.Element {
  const debouncedOnChangeValue = debounce((numericValue: number | null) => {
    onChangeValue(numericValue);
  }, 1000);
  return (
    <div {...getTestProps({component: 'MeasurementWithMixedValuesInput', element: 'root'})}>
      <FieldLabel label={label} htmlFor="volume-with-units" />
      <div className={css(styles.inputWrapper)}>
        <NumericInput
          className={classnames(css(styles.input), {
            [css(styles.errorBorder)]: errors.valueError?.level === PlateMapErrorLevel.ERROR,
            [css(styles.warningBorder)]: errors.valueError?.level === PlateMapErrorLevel.WARNING,
            [css(styles.infoBorder)]: errors.valueError?.level === PlateMapErrorLevel.INFO,
          })}
          dataTestAttrs={{component: 'MeasurementWithMixedValuesInput', element: 'numericInput'}}
          id="volume-with-units"
          min={0}
          onChange={({target: {numericValue}}) => {
            debouncedOnChangeValue(numericValue);
          }}
          placeholder={value === MULTIPLE_VALUES_SENTINEL ? MULTIPLE_VALUES_SENTINEL : 'Enter value'}
          value={value === MULTIPLE_VALUES_SENTINEL ? null : value}
        />
        <div
          className={css(styles.unitWrapper)}
          {...getTestProps({component: 'MeasurementWithMixedValuesInput', element: 'quantityUnitsSelector'})}
        >
          <SearchableUnitDropdown
            onSelect={(unit) => {
              onChangeUnit(unit ? {...unit, conversionFactor: unit.conversionFactor.toString()} : null);
            }}
            unitTypeFilter={unitTypeFilter}
            selectedId={unit && unit !== MULTIPLE_VALUES_SENTINEL ? unit.id : null}
            customPlaceholder={unit === MULTIPLE_VALUES_SENTINEL ? 'Multiple units' : 'Select...'}
            includeNone={true}
          />
        </div>
      </div>
      {errors.valueError != null && (
        <div
          className={classNames({
            [css(styles.errorText)]: errors.valueError.level === PlateMapErrorLevel.ERROR,
            [css(styles.warningText)]: errors.valueError.level === PlateMapErrorLevel.WARNING,
            [css(styles.infoText)]: errors.valueError.level === PlateMapErrorLevel.INFO,
          })}
        >
          {errors.valueError.message}
        </div>
      )}
      {errors.unitsError != null && (
        <div
          className={classNames({
            [css(styles.errorText)]: errors.unitsError.level === PlateMapErrorLevel.ERROR,
            [css(styles.warningText)]: errors.unitsError.level === PlateMapErrorLevel.WARNING,
            [css(styles.infoText)]: errors.unitsError.level === PlateMapErrorLevel.INFO,
          })}
        >
          {errors.unitsError.message}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  inputWrapper: {
    display: 'flex',
  },
  input: {
    marginRight: '4px',
  },
  unitWrapper: {
    flex: '1 0 auto',
  },
  errorBorder: {
    borderColor: ERROR_LEVEL_TO_COLOR[PlateMapErrorLevel.ERROR],
  },
  errorText: {
    color: ERROR_LEVEL_TO_COLOR[PlateMapErrorLevel.ERROR],
  },
  warningBorder: {
    borderColor: ERROR_LEVEL_TO_COLOR[PlateMapErrorLevel.WARNING],
  },
  warningText: {
    color: ERROR_LEVEL_TO_COLOR[PlateMapErrorLevel.WARNING],
  },
  infoBorder: {
    borderColor: ERROR_LEVEL_TO_COLOR[PlateMapErrorLevel.INFO],
  },
  infoText: {
    color: ERROR_LEVEL_TO_COLOR[PlateMapErrorLevel.INFO],
  },
});
